<template>
  <KkModal :is-visible="showActionModal" :width="modalWidth" @change="closeActionModal()">
    <div class="p-modal__content__header">{{ $t('labels.preview') }}</div>

    <Loading v-if="postLoading" />
    <div v-else class="content__body">
      <div :class="`postContent__${displayApprovalContent ? 'half' : 'full' }`">
        <div v-if="Object.keys(previewServiceTypes).length" class="postContent__tab">
          <KkTabList
            :menu-list="previewServiceTypes"
            :selected-tab-key="selectedServiceType"
            @click-menu="clickMenu($event)"
          />
        </div>
        <div class="postContent__body">
          <!-- アラート -->
          <div v-if="showSameContentAlert" class="sameContentAlert">
            <KkInfo icon="exclamation" alert>
              {{ $t('pages.CompaniesStorePost.sameContentAlert') }}
            </KkInfo>
          </div>
          <!-- 投稿予定時間 -->
          <div v-if="reservationDateTime" class="action__info">
            <div class="action__info-scheduleDateTime">{{ $t('pages.CompaniesStorePost.reservationDate') }}：<span>{{ scheduleDateTime }}</span></div>
            <div v-if="repeatingPost" class="action__info-repeatingPost">{{ $t('pages.CompaniesStorePost.repeatingPost') }}：<span>{{ repeatingPostName }}</span></div>
          </div>

          <!-- プレビュー -->
          <div v-if="selectedServiceType === 'googleMap'" class="action__preview">
            <KkGoogleMapPostPreview />
          </div>
          <div v-else-if="selectedServiceType === 'docomo'" class="action__preview">
            <KkDocomoPostPreview />
          </div>
          <div v-else-if="selectedServiceType === 'yahooMap'" class="action__preview">
            <KkYahooMapPostPreview />
          </div>

          <!-- 投稿先店舗一覧 -->
          <div class="action__urlPreviews">
            <div class="urlPreviewTitle">
              {{ $t('pages.CompaniesStorePost.storeCount', { count: selectedServicePostStores.length }) }}
            </div>
            <div class="p-table__content">
              <table>
                <thead>
                  <tr>
                    <th>
                      <span>{{ $t('models.parameter.storeName') }}</span>
                    </th>
                    <th v-if="showUrlOrPhoneOnStoresTable">
                      <span>{{ $t(`pages.CompaniesStorePost.${ showPhoneNumber ? 'phoneNumber' : 'url' }`) }}</span>
                    </th>
                  </tr>
                  <tr class="p-table__content__space">
                    <td :colspan="showUrlOrPhoneOnStoresTable ? 2 : 1" />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="postStore in selectedServicePostStores" :key="postStore.storeUuid">
                    <td>{{ postStore.name }}</td>
                    <template v-if="showUrlOrPhoneOnStoresTable">
                      <td v-if="showPhoneNumber">
                        {{ postStore.phoneNumber || $t('messages.noSettings') }}
                      </td>
                      <td v-else>
                        <a :href="showOfferType ? postStore.offerRedeemOnlineUrl : postStore.callToActionUrl" target="_blank">
                          {{ showOfferType ? postStore.offerRedeemOnlineUrl : postStore.callToActionUrl }}
                        </a>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <!-- 承認コメント -->
      <div v-if="displayApprovalContent" class="approvalContent">
        <h4>{{ $t('pages.CompaniesStorePost.approvalHistory') }}</h4>
        <div id="scroll" class="approvalHistoryContainer">
          <div v-if="post.notes.length === 0">
            <h4>{{ $t('pages.CompaniesStorePost.emptyApprovalHistory') }}</h4>
          </div>
          <div v-for="(note, i) in post.notes" :key="i" class="note">
            <h4>
              {{ noteHeader(note) }}
            </h4>
            <div class="comment" :class="noteBodyClassName(note)">{{ noteBodyComment(note) }}</div>
          </div>
        </div>
        <div>
          <h4>
            {{ $t('labels.approvalComment', { count: approvalRequestNotes.length, max: 400}) }}
            {{ $t('labels.optionalLabel') }}
          </h4>

          <textarea v-model="approvalRequestNotes" cols="30" rows="4" maxlength="400" class="c-textArea" />
        </div>
      </div>
    </div>

    <div class="p-modal__content__footer footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton color="secondary" outlined :text="$t('labels.cancel')" @click="closeActionModal" />
        </div>
        <template v-if="!isPreview">
          <template v-if="showSubmitActionModalType === 'approve'">
            <div class="buttonGroup__item">
              <KkButton
                color="primary"
                :text="$t('pages.CompaniesStorePost.rejectPost')"
                :disabled="submitProcessing"
                @click.once="postApprovalSubmit(false)"
              />
            </div>
            <div class="buttonGroup__item">
              <KkButton
                color="primary"
                :text="$t('pages.CompaniesStorePost.approvePost')"
                :disabled="submitProcessing"
                @click="handleApprovalSubmit(true)"
              />
            </div>
          </template>
          <template v-else>
            <div class="buttonGroup__item">
              <KkButton
                :text="submitButtonText"
                color="primary"
                :disabled="submitProcessing"
                @click="handleNewPostSubmit(true)"
              />
            </div>
          </template>
        </template>
      </div>
    </div>
  </KkModal>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { KkModal, KkButton } from '../../../../base'
import { KkInfo, KkTabList } from '../../../../../components/ui'
import { KkGoogleMapPostPreview, KkDocomoPostPreview, KkYahooMapPostPreview } from '../../../../../domains/post'
import moment from 'moment'
import Loading from '../../../../Loading'
import { dayOfWeek } from '../../../../../modules/dayOfWeekConst'

export default {
  name: 'KkPromotionCompaniesStorePostsActionModal',
  components: {
    Loading,
    KkModal,
    KkButton,
    KkInfo,
    KkGoogleMapPostPreview,
    KkDocomoPostPreview,
    KkYahooMapPostPreview,
    KkTabList,
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false
    },
    showActionModal: Boolean,
  },
  emits: ['close-action-modal','new-post-submit', 'post-approval-submit'],
  data() {
    return {
      termsConditionsToggle: false,
      approvalRequestNotes: '',
      selectedServiceType: ''
    }
  },
  computed: {
    ...mapState('companiesStorePosts', [
      'post',
      'reservationDateTime',
      'postLoading',
      'submitProcessing'
    ]),
    ...mapState({
      repeatingPostScheduleKind: state => state.companiesStorePosts.repeatingPostScheduleKind,
      repeatingPost: state => state.companiesStorePosts.repeatingPost,
    }),
    ...mapGetters('companiesStorePosts', [
      'showEventType',
      'showCallToActionType',
      'showOfferType',
      'showSubmitActionModalType'
    ]),
    modalWidth() {
      return this.displayApprovalContent ? '80%' : '50%'
    },
    displayApprovalContent() {
      if (this.isPreview) return false

      return (this.showSubmitActionModalType === 'approve' || this.showSubmitActionModalType === 'request')
    },
    previewServiceTypes() {
      const serviceTypes = {}
      if (this.post.stores.length) {
        serviceTypes.googleMap = this.$t('pages.CompaniesStorePost.service.googleMap')
      }
      if (this.post.docomoStores.length) {
        serviceTypes.docomo = this.$t('pages.CompaniesStorePost.service.docomo')
      }
      if (this.post.yahooMapStores.length) {
        serviceTypes.yahooMap = this.$t('pages.CompaniesStorePost.service.yahooMap')
      }
      return serviceTypes
    },
    scheduleDateTime() {
      return moment(this.reservationDateTime).format(this.$t('format.datetime.includeWday'))
    },
    dayOfWeekOfReservationDate() {
      const dowNumber = moment(this.reservationDateTime).day()
      return Object.values(dayOfWeek)[dowNumber]
    },
    dayOfWeekCountOfReservationDate() {
      return Math.floor((moment(this.reservationDateTime).toDate().getDate() - 1) / 7) + 1
    },
    repeatingPostName() {
      switch (this.repeatingPostScheduleKind){
      case 'daily':
        return '毎日'
      case 'weekly':
        return `毎週 ${this.dayOfWeekOfReservationDate}`
      case 'wdayIndex':
        return `毎月 第${this.dayOfWeekCountOfReservationDate}${this.dayOfWeekOfReservationDate}`
      case 'yearly':
        return `毎年 ${moment(this.reservationDateTime).format('M月D日')}`
      case 'weekday':
        return '毎週 (月〜金)'
      case 'monthly':
        return `毎月 ${moment(this.reservationDateTime).format('D日')}`
      }
      return null
    },
    submitButtonText() {
      let text
      switch (this.showSubmitActionModalType) {
      case 'post':
        text = this.$t('pages.CompaniesStorePost.post')
        break
      case 'draft':
        text = this.$t('pages.CompaniesStorePost.draft')

        break
      case 'request':
        text = this.$t('pages.CompaniesStorePost.apply')
        break
      }
      return text
    },
    // プレビューと下書き時には同一コンテンツアラートを出さない
    showSameContentAlert() {
      const isDraft = this.showSubmitActionModalType === 'draft'
      if (this.isPreview || isDraft) return false
      return this.post.sameContentsExist
    },
    showPhoneNumber() {
      return this.post.topicType !== 'OFFER' && this.post.callToAction.actionType === 'CALL'
    },
    showUrlOrPhoneOnStoresTable() {
      if (this.showSubmitActionModalType === 'draft') return false

      return this.showOfferType || (this.showCallToActionType && this.post.callToAction.actionType !== 'ACTION_TYPE_UNSPECIFIED')
    },
    selectedServicePostStores() {
      switch (this.selectedServiceType) {
      case 'googleMap':
        return this.post.stores
      case 'docomo':
        return this.post.docomoStores
      case 'yahooMap':
        return this.post.yahooMapStores
      }

      return []
    }
  },
  watch: {
    previewServiceTypes(value) {
      if (Object.keys(value).length) {
        this.selectedServiceType = Object.keys(value)[0]
      }
    }
  },
  created() {
    moment.locale(this.$i18n.locale)
  },
  methods: {
    ...mapActions({
      updateApprovalRequestNotes: 'companiesStorePosts/updateApprovalRequestNotes',
    }),
    closeActionModal() { this.$emit('close-action-modal') },
    handleNewPostSubmit(reservationDateTime) {
      if (!this.showSameContentAlert) {
        this.newPostSubmit(reservationDateTime)
        return
      }

      if (confirm(this.$t('pages.CompaniesStorePost.sameContentConfirm'))) {
        this.newPostSubmit(reservationDateTime)
      }
    },
    newPostSubmit(reservationDateTime) {
      this.updateApprovalRequestNotes(this.approvalRequestNotes)
      this.$emit('new-post-submit', reservationDateTime)
    },
    handleApprovalSubmit(isApprove) {
      if (!this.showSameContentAlert) {
        this.postApprovalSubmit(isApprove)
        return
      }

      if (confirm(this.$t('pages.CompaniesStorePost.sameContentConfirm'))) {
        this.postApprovalSubmit(isApprove)
      }
    },
    postApprovalSubmit(isApprove) {
      this.updateApprovalRequestNotes(this.approvalRequestNotes)
      this.$emit('post-approval-submit', isApprove)
    },
    formatDate(dateStr){
      return moment(dateStr).format(this.$t('format.datetime.default'))
    },
    noteHeader(note) {
      let category = 'applicantFormat'

      if(note.commenterName === '') {
        category = 'batchRejectFormat'
      } else if (note.kind === 'approver_comment') {
        category = 'approverFormat'
      }

      return this.$t(`pages.CompaniesStorePost.comment.${category}`, { commentedAt: this.formatDate(note.commentedAt), commenterName: note.commenterName })
    },
    // NOTE: bodyがない場合定型文を出す仕様だが、承認者でコメントがない場合一律差し戻しというのは
    // 別の別の申請パターンやステータス変更に対応した時に危うい。その際はバックエンドも含めて検討する必要があり。
    // 今のところ申請者、承認者とも一択なのでこの対応。
    noteBodyComment(note) {
      if (note.body.length === 0) {
        if (note.kind === 'applicant_comment') {
          return this.$t('pages.CompaniesStorePost.comment.requestedApproval')
        } else {
          return this.$t('pages.CompaniesStorePost.comment.revertedApproval')
        }
      }
      return note.body
    },
    noteBodyClassName(note) {
      if(note.commenterName === '' || note.body.length === 0) {
        return 'comment--system'
      } else if (note.kind === 'approver_comment') {
        return 'comment--approver'
      } else if (note.kind === 'applicant_comment') {
        return 'comment--applicant'
      }
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('#scroll')
        if(container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    clickMenu(ServiceType) {
      this.selectedServiceType = ServiceType
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  &__body {
    height: 50vh;
    display: flex;
    justify-content: center;
  }
}
.postContent {
  &__full {
    width: 100%;
  }

  &__half {
    width: 50%;
  }

  &__body {
    /* タブの高さで引く */
    height: calc(100% - 60px);
    padding: 32px;
    overflow-y: scroll;
  }
}
.approvalContent {
  width: 50%;
  height: 100%;
  background-color: #F8F9FA;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h4 {
    margin: 0;
  }
}
.approvalHistoryContainer {
  background-color: #FFFFFF;
  border: 1px solid #E3E3E3;
  box-sizing: border-box;
  border-radius: 4px;
  height: 65%;
  overflow-y: scroll;
  box-shadow: 0 0 0 rgba(63, 63, 68, .05);

  .note {
    margin: 16px;
    padding: 0 0 10px;

    h4 {
      margin: 0;
    }

    .comment {
      margin-top: 6px;
      padding: 8px 16px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 24px;
      width: fit-content;
      word-break: break-all;

      &--applicant {
        background-color: #ECEDF0;
      }

      &--approver {
        background-color: #46555F;
        color: #F8F9FA;
      }

      &--system {
        background-color: #FFFFFF;
        padding: 0;
      }
    }
  }
}

.action {
  &__info {
    display: flex;
    margin-bottom: 28px;
    justify-content: center;
    span {
      font-weight: bold;
    }

    &-scheduleDateTime {
      margin-right: 32px;
    }
  }

  &__preview {
    width: 70%;
    margin: 0 auto;
  }

  &__urlPreviews {
    margin: 32px 0 16px 0;

    .urlPreviewTitle {
      font-weight: bold;
      font-size: 12px;
      line-height: 12px;
      margin-bottom: 32px;
    }
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  &__info {
    margin: auto;
    height: 44px;
  }
}

.buttonGroup {
  display: flex;

  &__item {
    margin: 8px;
  }
}
.sameContentAlert {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}
</style>
